import { XrayRtcStateMachine } from '../state-machine';
import { XrayRtcConnectionEvent, XrayRtcConnectionInfo } from '../types';
import { log } from '../utils';

interface XrayRtcReporterOptions {
  reportIntervalSeconds?: number;
  rtcPeerConnection: RTCPeerConnection;
  stateMachine: XrayRtcStateMachine;
  timeoutSeconds: number;
  onReport?: (report: XrayRtcConnectionInfo) => void;
}

export default class XrayRtcReporter {
  public constructor(options: XrayRtcReporterOptions) {
    const { onReport, reportIntervalSeconds = 30, stateMachine } = options;

    window.setTimeout(async () => {
      const runReport = async () => {
        try {
          const report = await this.getReport(options);

          if (report && onReport) {
            onReport(report);
          }

          return true;
        } catch (error) {
          if (this.intervalId !== 0) {
            // make sure to stop report polling when in error state
            window.clearInterval(this.intervalId);
          }

          log('unable to get stats from peer connection', error as Error, 'error');

          stateMachine.emit(XrayRtcConnectionEvent.reporterError);

          return false;
        }
      };

      // immediately run initial report ASAP
      const initialReportSucceeded = await runReport();

      if (initialReportSucceeded) {
        // since initial report succeeded, wire-up a polling mechanism
        // so that reports are ran/returned on regular intervals
        this.intervalId = window.setInterval(runReport, reportIntervalSeconds * 1000);
      }
    });
  }

  public close() {
    window.clearInterval(this.intervalId);
  }

  private intervalId = 0;
  private resetSeconds = 0;

  private async getReport(options: XrayRtcReporterOptions): Promise<XrayRtcConnectionInfo | void> {
    const { rtcPeerConnection, stateMachine, timeoutSeconds } = options;

    const allStats = await rtcPeerConnection.getStats();

    window?.XRAY_CONSOLE_LOG_RTC_STATS && console.log(`Processing RTCStatsReport (size:${allStats.size})`);

    let bytesReceived = 0;
    let bytesSent = 0;
    let lastSentMs = 0;

    if (allStats) {
      allStats.forEach((stat: RTCStats, key) => {
        if (stat.type === 'candidate-pair') {
          const candidatePairStats = stat as RTCIceCandidatePairStats;
          window?.XRAY_CONSOLE_LOG_RTC_STATS &&
            console.log(`${key} RTCIceCandidatePairStats:`, candidatePairStats);

          bytesReceived = candidatePairStats.bytesReceived || 0;
          bytesSent = candidatePairStats.bytesSent || 0;

          lastSentMs = candidatePairStats.lastPacketSentTimestamp || 0;
        }
      });
    }

    const megabitsPerSecond = roundDecimals(
      bytesSent || (bytesReceived && stateMachine.dataChannelDurationSeconds)
        ? (bytesReceived + bytesSent) / stateMachine.dataChannelDurationSeconds / 125000
        : 0,
      4
    );

    const secondsOfInActivity = lastSentMs > 0 ? (Date.now() - lastSentMs) / 1000 - this.resetSeconds : 0;

    // reset
    this.resetSeconds = 0;

    return {
      bytesReceived,
      bytesSent,
      durationSeconds: stateMachine.dataChannelDurationSeconds,
      megabitsPerSecond,
      resetPendingTimeout: () => {
        // consumer reset pending timeout, so set the reset seconds
        // which are subtracted from seconds of inactivity, essentially
        // resulting in restting the pending timeout
        this.resetSeconds = secondsOfInActivity;
      },
      secondsBeforeTimeout: timeoutSeconds - secondsOfInActivity,
      status: stateMachine.status,
    };
  }
}

//
// helper functions
//

function roundDecimals(num: number, decimals: number) {
  return Number(Math.round(Number(`${num}e${decimals}e-${decimals}`)));
}
